import { Button, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { GridCellParams } from '@mui/x-data-grid';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import b64toBlob from 'b64-to-blob';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchTransitionalCareSummary, exportTransitionalCareSummary, MenuProps } from '../WidgetService';
import { TransitionalCareDateRangeEnum } from '../../../Enum/TransitionalCareDateRangeEnum';
import { FacilityTypeEnum } from '../../../Enum/FacilityTypeEnum';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import { generateRandomId, isDefaultDate } from '../../../utils/common';
import { dateFormatter } from '../../../utils/timeFormat';
import { parentUrl } from '../../../utils/constants';
import WidgetLoading from '../../SmartDashboard/Shared/WidgetLoading';
import '../../Widgets/Widgets.css';
import { AdmissionRuleEnum } from '../../../Enum/AdmissionRuleEnum';

const TransitionalCareSummaries = () => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const [teamLevelTcSummary, setTeamLevelTcSummary] = useState<[]>([]);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const {
        tcTeam,
        setTcTeam,
        tcFacility,
        setTcFacility,
        tcAdmissionRules,
        setTcAdmissionRules,
        tcDateFilter,
        setTcDateFilter,
        tcFromDate,
        setTcFromDate,
        tcToDate,
        setTcToDate,
        tcShowInActiveTeams,
        setTcShowInActiveTeams,
        tcTab,
        setTcTab,
    } = useWidgetContext();

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dateFilterList = [
        { Id: TransitionalCareDateRangeEnum.Today, Value: TransitionalCareDateRangeEnum.TodayDescription },
        { Id: TransitionalCareDateRangeEnum.Last1Month, Value: TransitionalCareDateRangeEnum.Last1MonthDescription },
        { Id: TransitionalCareDateRangeEnum.Last3Months, Value: TransitionalCareDateRangeEnum.Last3MonthsDescription },
        { Id: TransitionalCareDateRangeEnum.Custom, Value: TransitionalCareDateRangeEnum.CustomDescription },
    ];

    const faclityList = [
        { Id: FacilityTypeEnum.PhysicalHealth, Value: FacilityTypeEnum.PhysicalHealthDescription },
        { Id: FacilityTypeEnum.BehavioralHealthLevel1, Value: FacilityTypeEnum.BehavioralHealthLevel1Description },
        { Id: FacilityTypeEnum.BehavioralHealthLevel2, Value: FacilityTypeEnum.BehavioralHealthLevel2Description },
    ];

    const admissionRules = [
        { label: AdmissionRuleEnum.BH7DayDescription, value: AdmissionRuleEnum.BH7Day },
        { label: AdmissionRuleEnum.PH30DayDescription, value: AdmissionRuleEnum.PH30Day }
    ];

    useEffect(() => {
        if (
            tcDateFilter !== TransitionalCareDateRangeEnum.Custom ||
            (Boolean(tcFromDate) && Boolean(tcToDate))
        ) {
            setIsFetched(false);
            const changeFilter = async () => {
                let dateFrom = Boolean(tcFromDate) && tcDateFilter === TransitionalCareDateRangeEnum.Custom ? new Date(tcFromDate).toISOString() : '';
                let dateTo = Boolean(tcToDate) && tcDateFilter === TransitionalCareDateRangeEnum.Custom ? new Date(tcToDate).toISOString() : '';

                const data = await fetchTransitionalCareSummary(tcFacility, tcAdmissionRules, tcTab, tcTeam, tcDateFilter, dateFrom, dateTo, tcShowInActiveTeams);
                setTeamLevelTcSummary(data);
                setIsFetched(true);
            };
            changeFilter();
        }

        if (tcDateFilter !== TransitionalCareDateRangeEnum.Custom && Boolean(tcFromDate) && Boolean(tcToDate)) {
            setTcFromDate(null);
            setTcToDate(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsFetched, tcFacility, tcAdmissionRules, tcTab, tcTeam, tcDateFilter, tcFromDate, tcToDate, tcShowInActiveTeams]);

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTcTab(newValue);
    };

    const changeFromDate = (date: any) => {
        setTcFromDate(date);
    };

    const changeToDate = (date: any) => {
        setTcToDate(date);
    };

    const navigateToDetail = () => {
        history(`/provider/metricsummary/transitionalcaresummarydetail`);
    };

    const handleClick = (event) => {
        setShow(!show);
    };

    const getCellClass = (params: GridCellParams) => {
        if (tcTab !== 3) return '';

        const isContacted = params?.row?.IsContacted;

        if (isContacted === true) return 'green';
        if (isContacted === false) return 'red';

        return '';
    };

    const columns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Name',
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
                return <>{params.row.LastName} {params.row.FirstName}</>;
            },
            cellClassName: getCellClass,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DOB) ? '-' : dateFormatter(new Date(params.row.DOB))}</>;
            },
            cellClassName: getCellClass,
        },
        {
            field: 'Facility',
            headerName: 'Facility Name',
            minWidth: 300,
            flex: 1,
            cellClassName: getCellClass,
        },
        {
            field: 'AdmitDate',
            headerName: 'Admit Date',
            minWidth: 150,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.AdmitDate) ? '-' : dateFormatter(new Date(row.AdmitDate))}`,
            cellClassName: getCellClass,
        },
        ...(tcTab === 3
            ? [{
                field: 'DischargeDate',
                headerName: 'Discharge Date',
                minWidth: 150,
                flex: 1,
                valueGetter: (value, row) => `${isDefaultDate(row.DischargeDate) ? '-' : dateFormatter(new Date(row.DischargeDate))}`,
                cellClassName: getCellClass,
            }]
            : []),
        {
            field: 'LastCHPVisit',
            headerName: 'Last InPerson Appt Date',
            minWidth: 180,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.LastCHPVisit) ? '-' : dateFormatter(new Date(row.LastCHPVisit))}`,
            cellClassName: getCellClass,
        },
        ...(tcTab === 3
            ? [{
                field: 'IsContacted',
                headerName: 'Seen by Licensed Staff',
                minWidth: 180,
                flex: 1,
                valueGetter: (val: any) => {
                    return val === true || val === 'true' || val === 1 ? 'Yes' : 'No';
                },
                cellClassName: getCellClass,
            }]
            : [])
    ];

    const exportDetail = async () => {
        setDownLoading(true);
        let start = tcFromDate !== null ? new Date(tcFromDate).toDateString() : null;
        let end = tcToDate !== null ? new Date(tcToDate).toDateString() : null;
        const data = await exportTransitionalCareSummary(tcFacility, tcAdmissionRules, tcTeam, tcDateFilter, start, end, tcShowInActiveTeams);
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `TransitionalCareSummary - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'} ref={ref}>
                                <span className="pointer" onClick={navigateToDetail}>
                                    Transitional Care Summary
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="caption">This widget shows you all members with open transitional care whether inpatient or discharged.
                                            </Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                InPatient :
                                            </Typography>
                                            <Typography variant="caption">This shows the members who are currently documented to be inpatient at a facility and not yet discharged</Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Discharged :
                                            </Typography>
                                            <Typography variant="caption">This shows the members who are documented to be discharged and now needs CL follow-up and TC follow-up by a licensed NP or BHS dependent on admission diagnosis</Typography>
                                            <Typography marginTop={1} variant="subtitle2">
                                                7-day rule:
                                            </Typography>
                                            <Typography variant="caption">BH admission only post discharge.</Typography>
                                            <Typography marginTop={1} variant="subtitle2">
                                                30-day rule:
                                            </Typography>
                                            <Typography variant="caption">PH admission only post discharge.</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        <Grid size={4}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={tcTeam}
                                onChange={(id) => setTcTeam(id)}
                                showInactive={tcShowInActiveTeams}
                                onToggleInactive={setTcShowInActiveTeams}
                            />
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Facility</InputLabel>
                                <Select
                                    value={tcFacility}
                                    label="Facility"
                                    name="Facility"
                                    onChange={(e) => setTcFacility(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    {faclityList.map((facility) => (
                                        <MenuItem key={facility.Id} value={facility.Id}>
                                            {facility.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Admission Rule</InputLabel>
                                <Select
                                    disabled={tcTab !== 3}
                                    value={tcAdmissionRules}
                                    label="Admission Rule"
                                    name="Admission Rule"
                                    onChange={(e) => setTcAdmissionRules(e.target.value)}
                                    size="small"
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    {admissionRules.map((rule) => (
                                        <MenuItem key={rule.value} value={rule.value}>
                                            {rule.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select disabled={tcTab === 3} value={tcDateFilter} label="Date Filter" name="Date" onChange={(e) => setTcDateFilter(parseInt(e.target.value as any))}>
                                    {dateFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {tcDateFilter === TransitionalCareDateRangeEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={tcFromDate || null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={tcToDate || null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : null}
                    </Grid>
                    <Grid container alignItems="center" spacing={2} paddingLeft={2} paddingRight={2} paddingTop={2}>
                        <Grid size={12} display="flex" alignItems="center">

                            {/* Left side - Tabs */}
                            <Tabs value={tcTab} onChange={handleAlertsTabChange}>
                                <Tab value={1} label="InPatient" />
                                <Tab value={3} label="Discharged" />
                                <Tab value={2} label="ED" />
                            </Tabs>

                            {/* Right side - Button */}
                            <Button
                                sx={{ marginLeft: 'auto', minWidth: '120px' }}
                                onClick={exportDetail}
                                variant="contained"
                                color="success"
                            >
                                {downLoading ? 'Exporting...' : 'Export'}
                            </Button>

                        </Grid>
                    </Grid>
                    <Box display="flex" position="relative" height='calc(100vh - 285px)'>
                        {
                            !isFetched ? <WidgetLoading />
                                : teamLevelTcSummary.length > 0 ?
                                    <Grid container spacing={2} marginTop={3} sx={{ height: '100%', maxHeight: '380px', overflowY: 'auto', position: 'relative' }}>
                                        <DataGrid
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/transitional-care`, '_blank');
                                            }}
                                            rows={teamLevelTcSummary}
                                            columns={columns}
                                            paginationModel={{ pageSize: pageSize, page: page }}
                                            pageSizeOptions={[5]}
                                            onPaginationModelChange={(model) => {
                                                setPageSize(model.pageSize);
                                                setPage(model.page);
                                            }}
                                            getRowId={(row) => `${row.Id}-${row.AdmitDate}-${generateRandomId()}`}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                                [`& .green`]: {
                                                    color: 'green',
                                                },
                                                [`& .red`]: {
                                                    color: 'red',
                                                },
                                                [`& .yellow`]: {
                                                    color: 'gold',
                                                },
                                                [`& .black`]: {
                                                    color: 'rgba(0,0,0,0.87)',
                                                },
                                            }}
                                        />
                                    </Grid>
                                    : <Box display="flex" width="100%" justifyContent="center" alignItems="center">
                                        <Typography variant="h6">No Matching Records</Typography>
                                    </Box>
                        }
                    </Box>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default TransitionalCareSummaries;
